// CatalogSortDropdown — "Sort: <option> ▾" trigger that opens the
// shared DropdownMenu used everywhere else in the app (so positioning,
// click-outside, keyboard nav, native action sheet on iOS, and theming
// all come for free instead of being reinvented per surface).
//
// Shared by tools, tasks, and other catalog toolbars so sort controls stay
// visually consistent (replaces page-specific `*CatalogSortDropdown` copies).

import React, { useMemo, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';

const CatalogSortDropdown = ({ value, options, onChange, label = 'Sort', footer = null, extraActiveOptions = [], size = 'small' }) => {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const [hovered, setHovered] = useState(false);

  const active = useMemo(
    () => options.find(o => o.id === value)
      || extraActiveOptions.find(o => o.id === value)
      || options[0],
    [options, extraActiveOptions, value],
  );

  const styles = useMemo(() => StyleSheet.create({
    trigger: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingHorizontal: isXS ? 10 : 12,
      height: size === 'medium' ? 42 : 32,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    },
    triggerHovered: {
      backgroundColor: colors.solid_color_tertiary,
    },
  }), [colors, isXS, size]);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild={Platform.OS === 'web'}>
        <Pressable
          style={StyleSheet.flatten([styles.trigger, hovered && styles.triggerHovered])}
          onHoverIn={() => setHovered(true)}
          onHoverOut={() => setHovered(false)}
          accessibilityRole="button"
          accessibilityLabel={label}
          accessibilityHint={active?.label}
        >
          {!isXS && <Text size="xs" color="tertiary">{label}:</Text>}
          <View style={{ maxWidth: 140 }}>
            <Text size="xs" color="primary" weight="bold" numberOfLines={1}>
              {active?.label}
            </Text>
          </View>
          <Icon name="chevron-down" size={9} color={colors.text_color_tertiary} />
        </Pressable>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        className={Platform.OS === 'web' ? 'DropdownMenuContent' : undefined}
        align="end"
        sideOffset={5}
        style={{ minWidth: 200 }}
      >
        {options.map(opt => (
          Platform.OS === 'web' ? (
            <DropdownMenuItem
              key={opt.id}
              icon={opt.icon}
              iosIcon={opt.iosIcon}
              title={opt.label}
              isActive={value === opt.id}
              onSelect={() => onChange?.(opt.id)}
            />
          ) : (
            // Native menus only read children whose element type is
            // DropdownMenu.Item, so the wrapper is web-only here. Callers
            // passing a `footer` have to follow the same rule.
            <DropdownMenu.Item key={opt.id} onSelect={() => onChange?.(opt.id)}>
              {(value === opt.id || opt.iosIcon) && (
                <DropdownMenu.ItemIcon ios={{ name: value === opt.id ? 'checkmark' : opt.iosIcon }} />
              )}
              <DropdownMenu.ItemTitle>{opt.label}</DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
          )
        ))}
        {footer ? (
          <>
            <DropdownMenu.Separator
              className={Platform.OS === 'web' ? 'DropdownMenuSeparator' : undefined}
            />
            {footer}
          </>
        ) : null}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

CatalogSortDropdown.propTypes = {
  value: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
    iosIcon: PropTypes.string,
  })).isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium']),
  footer: PropTypes.node,
  extraActiveOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
    iosIcon: PropTypes.string,
  })),
};

export default React.memo(CatalogSortDropdown);
